/**
 * SuccessLane landing page — redesign per Spec v2 (14 sections)
 *
 * Design language extracted from Claude Design artifact 33bd7d1f and preserved
 * verbatim: Poppins font, deep-purple palette, rounded-pill nav, radial-gradient
 * hero, conic-rainbow rotating borders on testimonials, scroll-left tickers,
 * IntersectionObserver fade-in on section reveal.
 *
 * Content follows Spec v2 (uploaded PDF, Aug 2026):
 *   1. Trust bar (dark strip above nav)                     2. Sticky nav pill
 *   3. Dark hero (no badge, no deliverable pills)           4. Pain-chip block
 *   5. 4 differentiator cards                               6. Sticky-left process
 *   7. Recruiter panel (2 marquee rows)                     8. Before/After LinkedIn mockup
 *   9. Testimonials carousel                                10. Promise + pricing (2 cards)
 *  11. Dark CTA band                                        12. Two-path closing CTA
 *  13. FAQ (accordion)                                      14. Footer
 *
 * Actual checkout price still comes from /api/pricing/current so admin can adjust
 * without a redeploy; spec's ₹1,499 is the display default when the fetch fails.
 */
import API_BASE from '../config';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseUtmFromLocation, captureUtmFirstTouch } from '../services/utmTracking';

// Design system tokens (verbatim from artifact 33bd7d1f)
const c = {
  text:      '#1a1130',
  textMid:   '#4b4560',
  textSoft:  '#5b5470',
  textMute:  '#6b6580',
  textDim:   '#9691a8',
  purpleSoft:'#8a7fb0',
  purple:    '#7c3aed',
  purpleDeep:'#6d28d9',
  purpleMid: '#8b5cf6',
  gradient:  'linear-gradient(135deg, #8b5cf6, #7c3aed)',
  hilite:    'linear-gradient(90deg, #c4b5fd, #8b5cf6)',
  navy:      '#1a0c38',
  navy2:     '#2a1257',
  navyMid:   '#4a2691',
  bgWhite:   '#ffffff',
  bgLavender:'#f6f4fd',
  bgFaint:   '#efedf6',
  green:     '#22c55e',
  greenDeep: '#16a34a',
  greenLite: '#dcfce7',
  yellow:    '#fbbf24',
};

const CheckIcon = ({ size = 14, fill = '#fff' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
);
// Round gold-filled badge with a white checkmark — used to make premium
// pricing bullets stand out against a white card, since a flat gold
// checkmark (no fill behind it) has too little contrast to read on white.
const GoldCheckIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, flexShrink: 0 }}>
    <defs>
      <linearGradient id="sl-gold-badge" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F5D687" />
        <stop offset="100%" stopColor="#C89B2E" />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="12" fill="url(#sl-gold-badge)" />
    <path d="M9.5 16.2L5.8 12.5l1.3-1.3 2.4 2.4 5.4-5.4 1.3 1.3z" fill="#fff" />
  </svg>
);
const ArrowIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, flexShrink: 0 }}>
    <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const XIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill: '#c5bfe0', flexShrink: 0 }}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
  </svg>
);
// Hamburger / close — mobile nav toggle for the Pricing/About links that get
// hidden below 860px (there's no other way to reach them on a phone once
// .sl-nav-hide kicks in, so this menu is the only path to those pages).
const MenuIcon = ({ open = false, size = 20, stroke = '#1a0c38' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, flexShrink: 0 }}>
    {open ? (
      <path d="M6 6l12 12M18 6L6 18" stroke={stroke} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M4 7h16M4 12h16M4 17h16" stroke={stroke} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);
const ShieldIcon = ({ size = 26, fill = '#fff' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M12 2L3 7v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
  </svg>
);
// Shield-with-checkmark "certified" badge — used for trust/certification
// callouts, as distinct from the plain ShieldIcon used elsewhere.
const CertifiedIcon = ({ size = 14, fill = '#fff' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1.2 14.6L6.6 11.4l1.4-1.4 2.8 2.8 5.6-5.6 1.4 1.4-7 7z" />
  </svg>
);
const SearchIcon = ({ size = 26, fill = '#fff' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);
const KeyIcon = ({ size = 26, fill = '#fff' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" />
  </svg>
);
const CalendarIcon = ({ size = 26, fill = '#fff' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z" />
  </svg>
);
const LinkedInIcon = ({ size = 20, fill = '#0A66C2' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);
// Brand logo mark — official asset (frontend/public/images/brand/), used
// verbatim everywhere so it's pixel-identical across every screen. `height`
// sets the rendered size; width is intentionally left to `auto` so the
// image's own aspect ratio is preserved and it's never stretched/distorted.
const Logo = ({ height = 28 }) => (
  <img
    src="/images/brand/successlane-logo-mark.png"
    alt="SuccessLane"
    className="sl-logo"
    style={{ height, width: 'auto', display: 'block' }}
  />
);

/**
 * Circular avatar that overlays an <img> on top of an initials placeholder.
 * If the image fails to load (file not uploaded yet, wrong extension, 404),
 * the onError handler hides the <img> and the initials-with-coloured-bg
 * placeholder remains visible. No broken-image icon ever shows.
 *
 * `photo` is an optional URL (e.g. "/images/recruiters/st.jpg"). When it's
 * undefined the <img> isn't rendered at all — pure initials.
 */
const Avatar = ({ size, bg, color, initials, photo, fontSize, ring = null }) => (
  <div style={{ position: 'relative', width: size, height: size, borderRadius: '50%', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: fontSize || size * 0.36, flexShrink: 0, overflow: 'hidden', boxShadow: ring || undefined }}>
    {initials}
    {photo && (
      <img
        src={photo}
        alt=""
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    )}
  </div>
);

// Data ------------------------------------------------------------------------

const painChips = [
  "I've applied to 200+ roles",
  'Recruiters never respond',
  'I keep editing but nothing changes',
  "I'm qualified but invisible",
  "I don't know what's wrong",
  "I'm using ChatGPT and Claude but no results",
];

const differentiators = [
  {
    icon: <SearchIcon />,
    title: 'Reverse engineered',
    body: 'We study profiles of people who just landed your target role. Then we build yours from what actually worked.',
  },
  {
    icon: <KeyIcon />,
    title: 'Well Researched Keywords',
    body: 'The exact search terms recruiters type for your role, placed in your headline, summary, and skills.',
  },
  {
    icon: <ShieldIcon />,
    title: 'Recruiter certified',
    body: 'A recruiter who fills roles like yours reads every word and signs off before you see it.',
  },
  {
    icon: <CalendarIcon />,
    title: '100 day action plan',
    body: 'We provide customized 100 day action plan for posting, outreach and networking.',
  },
];

// Recruiter panel — real, named recruiters. `photo` follows the same
// convention as founders/advisors elsewhere on the site
// (public/images/recruiters/<slug>.jpg) — no photo files exist for these six
// yet, so the card falls back to initials until real headshots are added.
// `linkedin` is a genuine profile URL. `roles` (split from the old `specs`
// string) renders as individual "Hires for" chips per the panel redesign.
const recruiterCards = [
  { name: 'Sushma Jodge',      slug: 'sushma-jodge',      init: 'SJ', designation: 'Senior Recruitment Partner', company: 'Amazon',         roles: ['GCC', 'Tech', 'CXO'],                             linkedin: 'https://www.linkedin.com/in/sushma-jodge-bb7b967/' },
  { name: 'Ritika Kansal',     slug: 'ritika-kansal',     init: 'RK', designation: 'Senior Recruiter',           company: 'Browser Stack', roles: ['Non Tech', 'Finance & Accounts'],                 linkedin: 'https://www.linkedin.com/in/ritika-kansal-53b86995/' },
  { name: 'Kanaka Prakash',    slug: 'kanaka-prakash',    init: 'KP', designation: 'Principal Consultant',       company: 'IQVIA',         roles: ['Health care', 'Consulting', 'Operation'],         linkedin: 'https://www.linkedin.com/in/kanakaprakashnagaraj/' },
  { name: 'Yamini KN',         slug: 'yamini-kn',         init: 'YK', designation: 'Senior Recruiter',           company: 'Net Crackers',  roles: ['Tech', 'Data Science', 'AI/ML'],                  linkedin: 'https://www.linkedin.com/in/yamini-k-n-b03650b1/' },
  { name: 'Chandni Gupta',     slug: 'chandni-gupta',     init: 'CG', designation: 'Recruitment Advisor',        company: 'Bullzeye',      roles: ['Manufacturing', 'Leadership', 'Banking'],         linkedin: 'https://www.linkedin.com/in/chandniguptabullzeye/' },
  { name: 'Yoshita Choudhary', slug: 'yoshita-choudhary', init: 'YC', designation: 'Senior Recruiter',           company: 'Bullzeye',      roles: ['E commerce', 'Marketing', 'Sales'],               linkedin: 'https://www.linkedin.com/in/yoshita-choudhary-b63798141/' },
].map((r) => ({ ...r, photo: `/images/recruiters/${r.slug}.jpg` }));

// `photo` is served from public/images/testimonials/<firstname>.jpg — missing
// files fall back to the initials placeholder via the <Avatar> helper.
const testimonials = [
  {
    initials: 'NK',
    photo: '/images/testimonials/nikhil.jpg',
    name: 'Nikhil K.',
    role: 'Industry switch to Consumer Tech',
    outcome: 'Switched industries in 6 weeks',
    quote: `"I was stuck in manufacturing for 8 years. The rewrite positioned my operational experience for product roles. Two weeks after updating, a recruiter from a D2C brand reached out. I start next month."`,
  },
  {
    initials: 'AS',
    photo: '/images/testimonials/arjun.jpg',
    name: 'Arjun S.',
    role: 'Senior Product Manager',
    outcome: '5 offers, 24L hike',
    quote: `"Five offers in three weeks. The keywords they placed in my headline were exactly what recruiters were searching. I didn't apply to a single one of these roles. They found me."`,
  },
  {
    initials: 'RK',
    photo: '/images/testimonials/rohan.jpg',
    name: 'Rohan K.',
    role: 'Engineering Manager',
    outcome: '4 inbound messages in first week',
    quote: `"Four recruiters messaged me in the first week after the rewrite. I hadn't changed my job title or experience. They just rewrote how it was described. That was the difference."`,
  },
  {
    initials: 'PM',
    photo: '/images/testimonials/priya.jpg',
    name: 'Priya M.',
    role: 'Director of Operations',
    outcome: '3X profile views in 30 days',
    quote: `"At my level, roles aren't posted. They're filled through search. The rewrite made sure my profile showed up in those searches. Profile views tripled within a month."`,
  },
];

// Spec: rename "LinkedIn Playbook" → "100-day action plan" throughout
const faqs = [
  {
    q: 'Is the free evaluation actually free?',
    a: 'Yes. Fully free. No card, no signup, no sales call. Upload your profile, tell us your target role, and get a detailed recruiter-voice read.',
  },
  {
    q: "Why can't I just use ChatGPT for this?",
    a: 'You can, and it will give you fluent sentences. What it cannot do is tell you which words recruiters in your industry actually type into the search box, or take responsibility for the result. Every rewrite here is checked and certified by a recruiter who hires for your role.',
  },
  {
    q: 'How is this different from other LinkedIn services?',
    a: 'Most use AI or generic freelancers. We use a trained LinkedIn expert, backed by certification from an active recruiter in your exact industry. Nobody else offers that at this price.',
  },
  {
    q: "I'm at Director or VP level. Is this for me?",
    a: "Yes, and the stakes are higher at your level. The roles you want are rarely advertised. Almost all of them are filled through a recruiter search. Your profile either shows up in it or it doesn't.",
  },
  {
    q: "What's in the 100-day action plan?",
    a: 'A step-by-step system for staying visible after the rewrite. How to show up in recruiter search, what to post and how often, headline formulas you can update yourself, and outreach scripts for recruiters and hiring managers at your target companies.',
  },
  {
    q: 'Will my data be safe?',
    a: `Your profile is used only to generate your evaluation and rewrite. We don't store it, we don't share it, and we never touch your LinkedIn account in any way.`,
  },
  {
    q: 'The market is tough right now. Will fixing my LinkedIn even help?',
    a: `Recruiter searches don't stop in a bad market. They get more selective. Profiles that match the search terms still get the calls. That's true in every market. We make sure yours matches.`,
  },
];

// Recruiter panel — draggable/swipeable rail with eased prev/next buttons and
// a live scroll-progress bar (redesign per Claude Design "Recruiter Panel",
// project fd4c310c). Ported from the design's vanilla-JS Component class
// (pointer-drag + requestAnimationFrame easing) into React hooks/refs so the
// interaction matches the source 1:1 instead of falling back to plain
// scroll-snap.
const RecruiterPanel = ({ goAnalyze }) => {
  const railRef = useRef(null);
  const barRef = useRef(null);
  const rafRef = useRef(null);

  const measure = () => {
    const el = railRef.current;
    const bar = barRef.current;
    if (!el || !bar) return;
    const max = el.scrollWidth - el.clientWidth;
    bar.style.width = `${Math.max(12, Math.min(100, (el.clientWidth / el.scrollWidth) * 100))}%`;
    bar.style.marginLeft = `${max > 0 ? (el.scrollLeft / el.scrollWidth) * 100 : 0}%`;
  };

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    let pending = false;
    const onScroll = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; measure(); });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    measure();
    window.addEventListener('resize', measure);

    // Pointer-drag to scroll (desktop only — touch keeps native scrolling)
    let down = false, sx = 0, sl = 0, moved = false;
    const onPointerDown = (e) => {
      if (e.pointerType === 'touch') return;
      down = true; moved = false; sx = e.clientX; sl = el.scrollLeft;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      el.style.cursor = 'grabbing';
    };
    const onPointerMove = (e) => {
      if (!down) return;
      const d = e.clientX - sx;
      if (Math.abs(d) > 4) moved = true;
      el.scrollLeft = sl - d;
    };
    const onPointerUp = () => {
      if (!down) return;
      down = false; el.style.cursor = '';
      if (moved) {
        const blockClick = (e) => { e.preventDefault(); e.stopPropagation(); window.removeEventListener('click', blockClick, true); };
        window.addEventListener('click', blockClick, true);
      }
    };
    el.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const step = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const card = el.querySelector('article');
    const w = (card ? card.getBoundingClientRect().width : 248) + 18;
    const per = el.clientWidth < 640 ? 1 : 2;
    const max = el.scrollWidth - el.clientWidth;
    const from = el.scrollLeft;
    const target = Math.max(0, Math.min(max, from + dir * w * per));
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (Math.abs(target - from) < 1) return;
    el.style.scrollBehavior = 'auto';
    const t0 = performance.now(), dur = 380;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.scrollLeft = from + (target - from) * eased;
      if (p < 1) rafRef.current = requestAnimationFrame(tick); else rafRef.current = null;
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  return (
    <section style={{ background: c.bgLavender, padding: 'clamp(56px, 8vw, 96px) 0 clamp(60px, 8vw, 104px)', overflow: 'hidden' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(20px, 5vw, 40px)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, flexWrap: 'wrap', marginBottom: 40 }}>
          <div style={{ maxWidth: 620 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 'clamp(28px,4.4vw,50px)', lineHeight: 1.1, letterSpacing: '-1.4px', fontWeight: 800, color: c.text, textWrap: 'balance' }}>
              Meet the recruiters behind<br />your certification
            </h2>
            <p style={{ margin: 0, fontSize: 'clamp(16px,2vw,19px)', lineHeight: 1.65, color: c.textSoft, textWrap: 'pretty' }}>
              40+ active recruiters on our panel, matched to your domain.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 4 }}>
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous recruiters"
              className="sl-rail-nav-btn"
              style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid #e2d9f6', background: '#fff', color: c.purpleDeep, display: 'grid', placeItems: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(76,29,149,.06)', transition: 'all 180ms ease' }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24"><path d="M19 12H5m7 7l-7-7 7-7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next recruiters"
              className="sl-rail-nav-btn sl-rail-nav-btn-primary"
              style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid transparent', background: c.gradient, color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', boxShadow: '0 6px 18px rgba(124,58,237,.28)', transition: 'all 180ms ease' }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24"><path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <div
          ref={railRef}
          className="sl-recruiter-rail"
          style={{ display: 'flex', gap: 18, overflowX: 'auto', scrollbarWidth: 'none', padding: '8px clamp(20px,5vw,40px) 24px', maxWidth: 1200, margin: '0 auto' }}
        >
          {recruiterCards.map((p) => (
            <article key={p.slug} className="sl-recruiter-card" style={{ flex: '0 0 clamp(228px,74vw,248px)', background: '#fff', border: `1px solid ${c.bgFaint}`, borderRadius: 18, padding: 16, display: 'flex', flexDirection: 'column', gap: 13, boxShadow: '0 2px 10px rgba(26,17,48,.04)', transition: 'transform 220ms cubic-bezier(.22,1,.36,1), box-shadow 220ms ease, border-color 220ms ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                <div style={{ position: 'relative', flexShrink: 0, width: 72, height: 72 }}>
                  <Avatar size={72} bg="linear-gradient(150deg, #ede9fe, #ddd6fe)" color="#5b21b6" initials={p.init} photo={p.photo} fontSize={22} ring="0 0 0 2px #fff, 0 0 0 4px #ede9fe" />
                  <span title="Currently in-seat" style={{ position: 'absolute', right: 1, bottom: 1, width: 14, height: 14, borderRadius: '50%', background: c.green, border: '2.5px solid #fff' }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: c.text, letterSpacing: '-0.3px', lineHeight: 1.25 }}>{p.name}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 500, color: c.textMute, lineHeight: 1.35, marginTop: 2 }}>{p.designation}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={c.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M3 21h18M5 21V7l7-4 7 4v14M10 10h.01M14 10h.01M10 14h.01M14 14h.01" /></svg>
                    <span style={{ fontSize: 12, fontWeight: 700, color: c.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.company}</span>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.9px', textTransform: 'uppercase', color: c.textDim, marginBottom: 7 }}>Hires for</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {p.roles.map((r) => (
                    <span key={r} style={{ fontSize: 11, fontWeight: 600, color: c.textSoft, background: c.bgLavender, border: `1px solid ${c.bgFaint}`, padding: '3px 9px', borderRadius: 999, lineHeight: 1.4 }}>{r}</span>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 11, borderTop: `1px solid ${c.bgFaint}` }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: c.purpleDeep }}>
                  <ShieldIcon size={12} fill={c.purpleDeep} />
                  Certified reviewer
                </div>
                <a href={p.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn profile" className="sl-recruiter-li-btn" style={{ width: 28, height: 28, borderRadius: 8, background: '#f2f1f8', color: c.textSoft, display: 'grid', placeItems: 'center', flexShrink: 0, transition: 'all 180ms ease' }}>
                  <LinkedInIcon size={13} fill="currentColor" />
                </a>
              </div>
            </article>
          ))}

          <a
            href="/analyze"
            onClick={(e) => { e.preventDefault(); goAnalyze(); }}
            className="sl-recruiter-cta-card"
            style={{ flex: '0 0 clamp(228px,74vw,248px)', background: `linear-gradient(155deg, ${c.navy2}, ${c.navy})`, borderRadius: 18, padding: 22, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff', boxShadow: '0 8px 28px rgba(20,8,50,.28)', transition: 'transform 220ms cubic-bezier(.22,1,.36,1)', textDecoration: 'none' }}
          >
            <div>
              <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1, background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>40+</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,.86)', marginTop: 9, lineHeight: 1.5 }}>recruiters across tech, finance, healthcare, manufacturing and GCC hiring.</div>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 700, color: '#fff', marginTop: 20 }}>
              Get matched to yours
              <ArrowIcon size={14} />
            </div>
          </a>
        </div>

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(20px,5vw,40px)', display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ flex: 1, height: 3, background: '#e2dfeb', borderRadius: 999, overflow: 'hidden' }}>
            <div ref={barRef} style={{ height: '100%', borderRadius: 999, background: c.gradient, width: '35%', marginLeft: 0 }} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: c.textDim, whiteSpace: 'nowrap' }}>Drag or swipe to browse</div>
        </div>
      </div>
    </section>
  );
};

// Testimonials — two-pane arc carousel (redesign per Claude Design
// "Testimonials Arc", project ccfd20a8). Left pane arranges avatars along an
// arc (active one large and centered, neighbours smaller, off-arc ones
// faded out); right pane shows the active person's quote with pagination
// dots. Auto-rotates every 5s, pauses on hover, click-to-select on any
// avatar or dot. Ported from the design's vanilla-JS Component class
// (setInterval + CSS transitions) into React hooks/state.
//
// Below 768px the 440px-wide arc pane has no room next to readable quote
// text, so the arc geometry is replaced with a single centered
// photo/initials + quote + dots — same data and interaction, simplified
// layout for narrow screens (CSS toggles which markup renders; both share
// the same state so they never fall out of sync).
//
// Card height trimmed from the design's 440px to 320px — the source geometry
// left a lot of dead space above/below the arc and around the quote. CY/R
// scaled down proportionally (0.73x) so the arc keeps the same visual
// proportions inside the shorter pane instead of clipping.
const TESTIMONIAL_CARD_HEIGHT = 320;
const TESTIMONIAL_ARC = { CX: -66, CY: 160, R: 175, STEP: 32 };
const TESTIMONIAL_EASE = 'cubic-bezier(.16,1,.3,1)';

const TestimonialsArc = () => {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(true);
  const pausedRef = useRef(false);
  const countRef = useRef(0);
  const fadeTimerRef = useRef(null);
  const activeRef = useRef(0);
  activeRef.current = active;

  const advance = (next) => {
    if (next === activeRef.current) return;
    setVisible(false);
    clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      setActive(next);
      setVisible(true);
    }, 280);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      if (pausedRef.current) { countRef.current = 0; return; }
      countRef.current++;
      if (countRef.current >= 5) {
        countRef.current = 0;
        advance((activeRef.current + 1) % testimonials.length);
      }
    }, 1000);
    return () => {
      clearInterval(timer);
      clearTimeout(fadeTimerRef.current);
    };
  }, []);

  const select = (i) => { countRef.current = 0; advance(i); };
  const N = testimonials.length;
  const p = testimonials[active];

  return (
    <section style={{ padding: '48px 24px', background: c.bgLavender }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto 28px' }}>
          <h2 className="sl-testi-heading" style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px', marginBottom: 16, whiteSpace: 'nowrap' }}>
            Real people.{' '}
            <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Real recruiter calls.</span>
          </h2>
          <p style={{ fontSize: 19, fontWeight: 700, color: c.textMute, lineHeight: 1.7 }}>1928+ profile career upgraded.</p>
        </div>

        <div
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
          className="sl-testi-arc"
          style={{ background: '#fff', border: '1px solid #efe9fb', borderRadius: 28, boxShadow: '0 18px 44px rgba(30,15,60,.07)', display: 'grid', gridTemplateColumns: '440px 1fr', height: TESTIMONIAL_CARD_HEIGHT, overflow: 'hidden' }}
        >
          {/* Arc pane — desktop/tablet only (hidden below 768px via CSS) */}
          <div className="sl-testi-arc-pane" style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(160deg, #FDFDFD, #F5F4FA)' }}>
            <div style={{ position: 'absolute', left: -240, top: -15, width: 350, height: 350, border: '1.5px solid #ddd2f7', borderRadius: '50%' }} />
            {testimonials.map((t, i) => {
              let o = (i - active + N) % N;
              if (o > N / 2) o -= N;
              const angle = (o * TESTIMONIAL_ARC.STEP * Math.PI) / 180;
              const x = TESTIMONIAL_ARC.CX + TESTIMONIAL_ARC.R * Math.cos(angle);
              const y = TESTIMONIAL_ARC.CY + TESTIMONIAL_ARC.R * Math.sin(angle);
              const isActive = o === 0;
              const size = isActive ? 64 : 40;
              return (
                <div
                  key={t.name}
                  onClick={() => select(i)}
                  style={{ position: 'absolute', left: x - size / 2, top: y - size / 2, width: size, height: size, opacity: Math.abs(o) > 1 ? 0 : 1, cursor: 'pointer', zIndex: isActive ? 2 : 1, transition: `all 0.8s ${TESTIMONIAL_EASE}` }}
                >
                  <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: '#ede9fe', boxShadow: isActive ? '0 0 0 3px #7c3aed, 0 12px 32px rgba(124,58,237,.35)' : '0 0 0 1px #efe9fb', transition: `all 0.8s ${TESTIMONIAL_EASE}` }}>
                    <Avatar size={size} bg="#ede9fe" color={c.purpleDeep} initials={t.initials} photo={t.photo} fontSize={size * 0.32} />
                  </div>
                </div>
              );
            })}
            <div style={{ position: 'absolute', left: 160, top: 160, transform: 'translateY(-50%)', width: 200, opacity: visible ? 1 : 0, transition: 'opacity 0.28s ease' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: c.text, letterSpacing: '-0.4px' }}>{p.name}</div>
              <div style={{ fontSize: 12.5, color: c.textMute, marginTop: 2 }}>{p.role}</div>
              <div style={{ display: 'inline-block', marginTop: 8, background: c.greenLite, color: c.greenDeep, fontSize: 12, fontWeight: 600, padding: '4px 11px', borderRadius: 100 }}>{p.outcome}</div>
            </div>
          </div>

          {/* Simplified header — mobile only (below 768px) */}
          <div className="sl-testi-mobile-head" style={{ display: 'none', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '24px 24px 8px', opacity: visible ? 1 : 0, transition: 'opacity 0.28s ease' }}>
            <Avatar size={56} bg="#ede9fe" color={c.purpleDeep} initials={p.initials} photo={p.photo} fontSize={19} ring="0 0 0 3px #7c3aed, 0 10px 26px rgba(124,58,237,.35)" />
            <div style={{ fontSize: 17, fontWeight: 700, color: c.text, letterSpacing: '-0.3px', marginTop: 12 }}>{p.name}</div>
            <div style={{ fontSize: 13, color: c.textMute, marginTop: 2 }}>{p.role}</div>
            <div style={{ display: 'inline-block', marginTop: 8, background: c.greenLite, color: c.greenDeep, fontSize: 12, fontWeight: 600, padding: '4px 11px', borderRadius: 100 }}>{p.outcome}</div>
          </div>

          <div className="sl-testi-quote-pane" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '28px 56px 28px 16px' }}>
            <div style={{ fontSize: 60, fontWeight: 800, color: '#c4b5fd', lineHeight: 0.4, height: 24, userSelect: 'none' }}>&#8220;</div>
            <p style={{ fontSize: 19, lineHeight: 1.6, color: c.textSoft, margin: '14px 0 0', maxWidth: 560, textWrap: 'pretty', opacity: visible ? 1 : 0, transition: 'opacity 0.28s ease' }}>{p.quote}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 26, justifyContent: 'center' }}>
              {testimonials.map((t, i) => (
                <button
                  key={t.name}
                  onClick={() => select(i)}
                  aria-label={t.name}
                  style={{ width: i === active ? 26 : 8, height: 8, borderRadius: 100, border: 'none', padding: 0, cursor: 'pointer', background: i === active ? c.purple : '#ddd6f3', transition: `all 0.4s ${TESTIMONIAL_EASE}` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------------------------------

const NewLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(0); // first item open by default (spec)

  // UTM first-touch capture for visitors landing straight on "/" with a
  // plain ?utm_source=&utm_campaign=&utm_term= link (vanity /{source}/{slug}/{term}
  // links are captured by UtmLandingResolver instead — this is a no-op then,
  // since captureUtmFirstTouch never overwrites an existing fresh first touch).
  useEffect(() => {
    captureUtmFirstTouch(parseUtmFromLocation(location.pathname, location.search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // spec default (1499); overridden by /api/pricing/current if reachable.
  // `compareAt` is the generic "strike-through" value from the backend —
  // it's the standard price when an internal offer is live, OR the
  // market-rate price when admin is comparing against market instead
  // (routes/pricing.py's compare_at_price). Reading this one generic field
  // means this component doesn't need to know or care which of the two
  // mutually-exclusive modes is active — it renders identically either way.
  const [pricing, setPricing] = useState({ effective: 1499, compareAt: null });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // hamburger dropdown (Pricing/About) below 860px

  // Pull live pricing so admin can override the display + checkout stays in sync
  useEffect(() => {
    fetch(`${API_BASE}/api/pricing/current`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const price = data?.price;
        if (price && Number.isFinite(Number(price))) {
          setPricing({
            effective: Number(price),
            compareAt: data?.compare_at_price ? Number(data.compare_at_price) : null,
          });
        }
      })
      .catch(() => {});
  }, []);
  const effectivePrice = pricing.effective;
  const discountPct = pricing.compareAt
    ? Math.round((1 - pricing.effective / pricing.compareAt) * 100)
    : 0;

  // IntersectionObserver fade-in — skip hero (renders instant), animate every
  // subsequent section as it scrolls into view.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const supported = 'IntersectionObserver' in window;
    if (!supported) return;
    const blocks = Array.from(root.children).slice(1);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.opacity = '1';
            e.target.style.transform = 'none';
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }
    );
    blocks.forEach((el) => {
      el.style.transition = 'opacity .8s cubic-bezier(.16,1,.3,1), transform .8s cubic-bezier(.16,1,.3,1)';
      el.style.opacity = '0';
      el.style.transform = 'translateY(34px)';
      io.observe(el);
    });

    // Separate observer just for the pain-chip panel: add `.animate` when it
    // scrolls in-view so each chip fires its `sl-chip-fall` keyframe.
    const chipPanel = root.querySelector('.sl-chip-panel');
    let chipIO;
    if (chipPanel) {
      chipIO = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              chipPanel.classList.add('animate');
              chipIO.unobserve(e.target);
            }
          });
        },
        { threshold: 0.25 }
      );
      chipIO.observe(chipPanel);
    }

    return () => { io.disconnect(); chipIO && chipIO.disconnect(); };
  }, []);

  const goAnalyze = () => navigate('/analyze');
  const goCheckout = () => navigate('/checkout');

  return (
    // Root has NO overflow:hidden/clip — that would break position:sticky on
    // the process section's left column. Marquee wrappers each carry their own
    // overflow:hidden so wide tracks don't leak into the page anyway.
    <div ref={rootRef} style={{ fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, sans-serif", color: c.text, lineHeight: 1.6, background: c.bgWhite, WebkitFontSmoothing: 'antialiased' }}>

      {/* Global CSS for keyframes, marquee, grids, responsive rules */}
      <style>{`
        @keyframes scroll-left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .sl-marquee-track { animation: scroll-left var(--sl-dur,32s) linear infinite; }
        .sl-marquee-reverse { animation-direction: reverse; }
        .sl-marquee-wrap:hover .sl-marquee-track { animation-play-state: paused; }
        .sl-cta-primary:hover, .sl-nav-cta:hover { transform: translateY(-1px); box-shadow: 0 18px 40px rgba(124,58,237,.55); }
        /* Recruiter panel (redesign) — rail drag cursor, card lift, nav-button
           and LinkedIn-button hover states ported from the design's
           style-hover attributes since inline styles can't express :hover. */
        .sl-recruiter-rail { cursor: grab; }
        .sl-recruiter-card:hover { transform: translateY(-5px); box-shadow: 0 18px 40px rgba(76,29,149,.14); border-color: #ddd6fe; }
        .sl-recruiter-cta-card:hover { transform: translateY(-5px); }
        .sl-recruiter-li-btn:hover { background: #0a66c2; color: #fff; }
        .sl-rail-nav-btn:hover:not(.sl-rail-nav-btn-primary) { background: #ede9fe; border-color: #c4b5fd; transform: translateY(-1px); }
        .sl-rail-nav-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(124,58,237,.36); }
        .sl-cta-primary, .sl-nav-cta { transition: transform .18s, box-shadow .18s; }
        /* Defensive fix for a classic flexbox overflow bug: a flex row's
           anonymous text children default to min-width:auto (= their full
           one-line content width), so on a narrow container the text+icon
           silently overflow past the button's edge instead of wrapping.
           Forcing flex-wrap + min-width:0 lets the row wrap onto a 2nd line
           as a last resort instead of clipping invisibly off-screen. */
        .sl-cta-primary, .sl-nav-cta { flex-wrap: wrap; min-width: 0; }
        .sl-nav-cta-short { display: none; }

        /* Same class of bug, but for CSS Grid: a grid item's default
           min-width:auto resolves to its *automatic minimum size* (roughly
           its content's un-shrunk footprint), which can force a 1fr track
           to grow past the space actually available — the item (and every
           line of text inside it) then renders wider than the viewport and
           silently gets clipped off-screen by the global overflow-x:clip,
           with no wrapping and no scrollbar to reveal it. min-width:0 lets
           the item shrink to its assigned track size so text wraps normally. */
        .sl-feature-grid > *, .sl-process-split > *,
        .sl-pricing-cards > *, .sl-closing-grid > * { min-width: 0; }
        .sl-nav-link:hover { color: ${c.purple}; }
        .sl-feature-card { outline: 2px solid transparent; outline-offset: 2px; transition: outline-color .2s, transform .2s; }
        .sl-feature-card:hover { outline-color: ${c.text}; transform: translateY(-2px); }
        .sl-faq-btn:hover { color: ${c.purple}; }

        /* --- Falling pain-chips 3D animation ------------------------------
           Multi-step gravity feel:
             0-25%  : accelerating fall from above (opacity ramp)
             55-70% : chip hits the ground with a small overshoot + tilt
             85%    : recoil / small hop after impact
             100%   : settle at final resting rotation
           Duration is deliberately long (1.8s) so the drop reads as physical,
           not snappy. Cubic-bezier flavour = accelerate-in, small elastic-out.
        */
        @keyframes sl-chip-fall {
          0% {
            opacity: 0;
            transform: translate3d(var(--sl-drift-x, 0), -620px, -260px)
                       rotateX(var(--sl-rot-x-init, -50deg))
                       rotateZ(calc(var(--sl-rot-final, 0deg) * 3.4));
          }
          25% { opacity: 1; }
          65% {
            opacity: 1;
            /* Ground impact — small downward overshoot + a nose-tilt */
            transform: translate3d(0, 22px, 0)
                       rotateX(10deg)
                       rotateZ(calc(var(--sl-rot-final, 0deg) * 1.25));
          }
          82% {
            /* Recoil hop */
            transform: translate3d(0, -6px, 0)
                       rotateX(-2deg)
                       rotateZ(var(--sl-rot-final, 0deg));
          }
          100% {
            opacity: 1;
            transform: translate3d(0, 0, 0) rotateX(0)
                       rotateZ(var(--sl-rot-final, 0deg));
          }
        }
        /* Chips are invisible + shifted above the panel by default so scrolling
           past a not-yet-animated section doesn't reveal them. The .animate
           class (added by IntersectionObserver on the panel) starts the drop. */
        .sl-falling-chip {
          opacity: 0;
          transform: translate3d(0, -620px, -260px) rotateX(-50deg);
        }
        .sl-chip-panel.animate .sl-falling-chip {
          animation: sl-chip-fall 1.8s cubic-bezier(0.5, 0.05, 0.55, 1)
                     var(--sl-delay, 0s) forwards;
        }
        /* Faint "ground" shadow that sits just below the chip cloud so
           landings read as touching a surface, not empty space. Positioned
           at the very bottom of the panel — panel now has only 26px bottom
           padding so the bottom-row chips visually sit ON this shadow. */
        .sl-chip-panel::after {
          content: '';
          position: absolute;
          left: 10%;
          right: 10%;
          bottom: 4px;
          height: 10px;
          border-radius: 50%;
          background: radial-gradient(ellipse at center,
                        rgba(80,40,140,.14) 0%,
                        rgba(80,40,140,.05) 55%,
                        transparent 75%);
          filter: blur(5px);
          pointer-events: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .sl-falling-chip { opacity: 1 !important; transform: none !important; animation: none !important; }
        }

        /* --- Responsive breakpoints -------------------------------------- */
        @media (max-width: 860px) {
          .sl-authority-grid { grid-template-columns: 1fr !important; gap: 44px !important; }
          .sl-feature-grid { grid-template-columns: 1fr !important; }
          .sl-pricing-cards { grid-template-columns: 1fr !important; }
          .sl-process-split { grid-template-columns: 1fr !important; gap: 40px !important; }
          .sl-process-left { position: static !important; }
          .sl-hero-chips { gap: 14px !important; }
          .sl-nav-hide { display: none !important; }
          .sl-nav-hamburger { display: flex !important; }
          .sl-closing-grid { grid-template-columns: 1fr !important; }
          /* Testimonials arc: the 440px avatar-arc pane has no room next to
             readable quote text below tablet width, so stack panes and swap
             the arc for a single centered photo/name header instead. */
          .sl-testi-arc { grid-template-columns: 1fr !important; height: auto !important; }
          .sl-testi-arc-pane { display: none !important; }
          .sl-testi-mobile-head { display: flex !important; }
          .sl-testi-quote-pane { padding: 8px 28px 40px !important; align-items: center; text-align: center; }
          .sl-testi-quote-pane > div:first-child { align-self: center; }
        }
        @media (max-width: 640px) {
          /* Tighter section padding on phones so content isn't cramped by gutters.
             Vertical padding is cut roughly in half too — a phone's viewport is
             short, so repeating 72-96px of top+bottom breathing room per section
             eats a much bigger share of visible screen than on desktop, and is
             the main reason the page reads as excessively long while scrolling. */
          section { padding-left: 18px !important; padding-right: 18px !important; padding-top: 36px !important; padding-bottom: 36px !important; overflow-x: clip; }
          /* Nav pill: shrink + let logo/button share the row without wrapping mid-word.
             Scoped to nav > .sl-nav-bar (the logo/CTA/hamburger row) specifically —
             NOT nav > div, which also matches .sl-nav-dropdown (the Pricing/About
             mobile menu, also a direct child of <nav>). That was force-squashing
             the dropdown to 60px too, so its second link (About) had nowhere to
             go but overflow outside the nav pill entirely, rendering over the
             hero section behind it instead of inside the white dropdown. */
          nav > .sl-nav-bar { padding: 0 14px !important; height: 60px !important; gap: 10px !important; }
          .sl-logo { height: 23px !important; }
          /* flex-wrap: nowrap overrides the shared .sl-cta-primary, .sl-nav-cta
             rule (flex-wrap: wrap) added for the hero's full-width CTAs. On the
             nav button specifically, that wrap let the short "Evaluate" label
             and its arrow icon stack into two rows, making the button taller
             than the 60px pill and spilling out of it. The nav CTA never needs
             to wrap — it already swaps to a short one-word label at narrow
             widths precisely so it always fits on one line. */
          .sl-nav-cta { padding: 10px 14px !important; font-size: 13px !important; gap: 5px !important; white-space: nowrap !important; flex-wrap: nowrap !important; }
          /* Hero: smaller H1, tighter spacing, stack CTAs vertically */
          h1 { font-size: clamp(28px, 8vw, 42px) !important; letter-spacing: -1px !important; }
          h2 { font-size: clamp(24px, 6.5vw, 32px) !important; letter-spacing: -.8px !important; }
          .sl-testi-heading { white-space: normal !important; }
          /* Testimonial card on phones: tighten header + quote padding so the
             whole card reasonably fits a short phone viewport (e.g. iPhone
             SE, 667px tall) on one scroll. Avatar itself is sized in JS
             (TESTIMONIAL_MOBILE_AVATAR), not CSS, since Avatar's width/height
             are inline styles. */
          .sl-testi-mobile-head { padding: 16px 24px 4px !important; }
          .sl-testi-quote-pane { padding: 4px 24px 28px !important; }
          .sl-testi-quote-pane p { font-size: 16px !important; margin-top: 10px !important; }
          .sl-testi-quote-pane > div:first-child { font-size: 44px !important; height: 18px !important; }
          .sl-testi-quote-pane > div:last-child { margin-top: 16px !important; }
          h3 { font-size: 17px !important; }
          .sl-chip-panel { padding: 56px 18px 24px !important; min-height: 220px !important; }
          /* Column, not wrapped row: each chip gets its own full-width line
             instead of auto-width pills that pack differently per row. The
             auto-width + center-justify approach (fine on desktop, where
             there's room for a scattered "pile" of short pills) reads as a
             jagged, uneven stack once flattened to one-per-row on a narrow
             screen — every chip a different width with its left edge landing
             in a different place. Stretching every chip to the full row width
             lines every left edge up, so it reads as one clean list instead. */
          .sl-chip-cloud { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
          /* Rotation is dropped on mobile (not just set to 0 via the JS array —
             the CSS var wins via !important) because the longest chip wraps to
             2 lines on narrow screens, and a rotated 2-line chip's rendered
             corners bleed into the row above/below no matter how much gap is
             set, since gap is computed from the un-rotated box. Flat chips
             make the gap value 100% reliable instead of a guess. */
          .sl-falling-chip { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; justify-content: flex-start !important; font-size: 13px !important; padding: 12px 16px !important; gap: 10px !important; white-space: normal !important; margin-left: 0 !important; --sl-rot-final: 0deg !important; }
          .sl-falling-chip svg { width: 17px !important; height: 17px !important; flex-shrink: 0; }
          /* CTAs: full-width, centered, and allowed to wrap onto 2 lines as a
             last resort rather than overflow off-screen */
          .sl-cta-primary { width: 100%; justify-content: center !important; padding: 15px 20px !important; text-align: center; white-space: normal !important; }
          /* Company-logos strip: tighter gap, smaller sizes */
          .sl-marquee-track > span { font-size: 28px !important; gap: 36px !important; }
          /* Process timeline (the "How It Works" numbered steps): the rail's
             paddingLeft:88 (plus the dot's left:-72 and the line's left:31,
             both offsets relative to that padding) is sized for desktop/
             tablet, where 88px is a small sliver of the available width. On
             a ~360px phone it eats ~24% of the content column, squeezing
             every step card — and everything inside it (the "Target role"
             mockup, the resume/LinkedIn chips) — into far too little space.
             Shrinking the gutter, dot, and card padding together (not just
             the gutter alone) keeps the dot correctly aligned on the rail
             instead of drifting off it. */
          .sl-timeline-rail { padding-left: 48px !important; }
          .sl-timeline-line { left: 25px !important; }
          .sl-timeline-dot { left: -38px !important; width: 30px !important; height: 30px !important; font-size: 13px !important; top: 4px !important; }
          .sl-timeline-card { padding: 20px !important; }
          /* Pricing cards: less padding */
          .sl-pricing-cards > div { padding: 30px !important; }
          /* Founders + closing option cards */
          .sl-closing-grid > div { padding: 28px !important; }
          /* Footer: stack copyright over links */
          footer > div { flex-direction: column !important; align-items: flex-start !important; }
        }
        @media (max-width: 480px) {
          /* At the narrowest widths, swap the nav CTA to a short label so the
             logo never gets squeezed into wrapping. */
          .sl-nav-cta-full { display: none !important; }
          .sl-nav-cta-short { display: inline !important; }
          .sl-logo { height: 20px !important; }
        }
        @media (max-width: 400px) {
          h1 { font-size: 26px !important; }
        }
      `}</style>

      {/* ============== SECTIONS 2 + 3: Nav + Hero (dark hero region) ============== */}
      <div style={{ background: `radial-gradient(1200px 700px at 50% -100px, ${c.navyMid} 0%, ${c.navy2} 42%, ${c.navy} 100%)`, backgroundColor: c.navy, padding: '18px 18px 48px' }}>
        {/* Sticky nav pill */}
        <nav style={{ position: 'sticky', top: 12, zIndex: 30, maxWidth: 1240, margin: '0 auto', background: '#fff', borderRadius: 22, boxShadow: '0 20px 50px rgba(20,8,50,.35)' }}>
          <div className="sl-nav-bar" style={{ padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 74 }}>
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
              <Logo />
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
              <a href="#pricing" className="sl-nav-link sl-nav-hide" style={{ color: c.textMid, fontSize: 15, fontWeight: 600, textDecoration: 'none' }}>Pricing</a>
              <a href="/about" className="sl-nav-link sl-nav-hide" style={{ color: c.textMid, fontSize: 15, fontWeight: 600, textDecoration: 'none' }}>About</a>
              <button onClick={goAnalyze} className="sl-nav-cta" style={{ background: c.gradient, color: '#fff', padding: '12px 22px', borderRadius: 13, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 8px 20px rgba(124,58,237,.4)', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                <span className="sl-nav-cta-full">Get Free Evaluation</span>
                <span className="sl-nav-cta-short">Evaluate</span>
                <ArrowIcon />
              </button>
              {/* Hamburger — only visible below 860px (.sl-nav-hamburger);
                  the only way to reach Pricing/About once those links hide. */}
              <button
                onClick={() => setMobileMenuOpen((v) => !v)}
                className="sl-nav-hamburger"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, border: `1px solid ${c.bgFaint}`, background: '#fff', cursor: 'pointer', flexShrink: 0, padding: 0 }}
              >
                <MenuIcon open={mobileMenuOpen} />
              </button>
            </div>
          </div>

          {/* Mobile dropdown — Pricing + About, only rendered while open */}
          {mobileMenuOpen && (
            <div className="sl-nav-dropdown" style={{ borderTop: `1px solid ${c.bgFaint}`, padding: '10px 16px 14px', display: 'flex', flexDirection: 'column' }}>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} style={{ color: c.text, fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 12px' }}>Pricing</a>
              <a href="/about" onClick={() => setMobileMenuOpen(false)} style={{ color: c.text, fontSize: 16, fontWeight: 600, textDecoration: 'none', padding: '12px 12px' }}>About</a>
            </div>
          )}
        </nav>

        {/* Hero content */}
        <section style={{ maxWidth: 940, margin: '0 auto', padding: '74px 24px 0', textAlign: 'center' }}>
          {/* "Built by LinkedIn-certified recruiters" — was a standalone dark
              strip above the nav; moved here as a proper icon+text pill so it
              reads as a trust badge leading the headline, together with the
              avatar/stars proof line right below it. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 100, padding: '9px 18px 9px 14px', marginBottom: 18 }}>
            <CertifiedIcon fill={c.green} size={16} />
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, letterSpacing: '.2px' }}>Built by LinkedIn-certified recruiters</span>
          </div>

          <h1 style={{ fontSize: 'clamp(38px,6vw,72px)', fontWeight: 800, lineHeight: 1.04, letterSpacing: '-2px', color: '#fff', maxWidth: 920, margin: '0 auto 26px', textWrap: 'balance' }}>
            The fastest way to get{' '}
            <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              recruiter calls and InMails
            </span>{' '}
            from LinkedIn.
          </h1>
          <p style={{ fontSize: 19, color: 'rgba(255,255,255,.72)', maxWidth: 720, margin: '0 auto 40px', lineHeight: 1.7 }}>
            At SuccessLane, top recruiters rewrite and certify your LinkedIn profile. We give you a 100-day action plan to 4X your visibility.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14, marginBottom: 16 }}>
            <button onClick={goAnalyze} className="sl-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: c.gradient, color: '#fff', padding: '17px 36px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 14px 34px rgba(124,58,237,.45)', fontFamily: 'inherit' }}>
              Get My Free Evaluation <ArrowIcon size={18} />
            </button>
            <a href="#how-it-works" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.18)', color: '#fff', padding: '17px 34px', borderRadius: 14, fontSize: 16, fontWeight: 700, textDecoration: 'none' }}>
              See How It Works
            </a>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,.5)' }}>Free. No card. No signup. Two minutes.</p>

          {/* Trust badge — avatar stack + stars, trails the CTAs as social
              proof (moved back down here; it had briefly been placed above
              the H1, which read wrong). */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 32 }}>
            <div style={{ display: 'flex' }}>
              {[
                { i: 'NK', bg: '#ede9fe', color: '#6d28d9' },
                { i: 'AS', bg: '#e0e7ff', color: '#4338ca' },
                { i: 'RK', bg: '#f3e8ff', color: '#7e22ce' },
                { i: 'PM', bg: '#dbeafe', color: '#1d4ed8' },
              ].map((a, idx) => (
                <span key={a.i} style={{ width: 36, height: 36, borderRadius: '50%', background: a.bg, color: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, border: `2px solid ${c.navy2}`, marginLeft: idx === 0 ? 0 : -10 }}>{a.i}</span>
              ))}
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: c.yellow, fontSize: 13, letterSpacing: 2 }}>★★★★★</div>
              <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                <strong>1,928</strong> professionals fixed this. You're next.
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ============== SECTION 5: Pain chips (section 4 industry strip is DELETED per Spec v2) ============== */}
      <section style={{ padding: '56px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px', marginBottom: 16 }}>
              Applying to every single job and{' '}
              <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>hearing nothing?</span>
            </h2>
            <p style={{ fontSize: 19, color: c.textMute, lineHeight: 1.7 }}>
              The problem is not your career. It's how your profile reads in recruiter search. We fix that.
            </p>
          </div>

          {/* Lavender panel with pain chips that fall from the top in 3D on
              scroll-in. Each chip drops from above viewport with a randomised
              initial X drift + X-axis tumble, lands with a slight overshoot,
              then settles at its final flex-layout position. Chips are in a
              standard flex-wrap so they never overlap once landed. */}
          <div className="sl-chip-panel" style={{ background: 'linear-gradient(160deg,#fbece2 0%,#efe6fb 45%,#e8defb 100%)', borderRadius: 28, padding: '92px 32px 32px', minHeight: 300, boxShadow: '0 30px 70px rgba(124,58,237,.12)', position: 'relative', overflow: 'hidden', perspective: '1400px', display: 'flex', alignItems: 'flex-end' }}>
            <div className="sl-chip-cloud" style={{ position: 'relative', width: '100%', maxWidth: 940, margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-end', gap: '22px 12px', transformStyle: 'preserve-3d' }}>
              {painChips.map((chip, i) => {
                // Per-chip randomised motion: drop delay, initial X drift,
                // initial X-axis tumble, final resting rotation
                const rots      = [7, -5, 4, -6, 6, -4];
                const drifts    = [-40, 55, -30, 45, -50, 35];
                const rotXInits = [-52, -38, -60, -44, -55, -42];
                // Small negative pull-ins so neighbouring chips visually
                // touch instead of floating in a neat evenly-gapped grid —
                // combined with each chip's resting rotation this reads as a
                // natural scattered pile rather than a UI grid. Kept small on
                // purpose: anything larger let a rotated chip's corner cover
                // the text of the chip next to it, making it unreadable.
                const overlaps  = [0, -4, -6, -4, -6, -5];
                const rot       = rots[i] || 0;
                const drift     = drifts[i] || 0;
                const rotXInit  = rotXInits[i] || -48;
                const overlap   = overlaps[i] || 0;
                const delay     = i * 0.14; // stagger 140ms per chip
                return (
                  <span
                    key={chip}
                    className="sl-falling-chip"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '13px 22px 13px 14px',
                      borderRadius: 100,
                      background: '#fff',
                      fontSize: 15,
                      color: '#2b2440',
                      fontWeight: 600,
                      boxShadow: '0 12px 26px rgba(30,15,60,.14)',
                      whiteSpace: 'nowrap',
                      transformStyle: 'preserve-3d',
                      willChange: 'transform, opacity',
                      marginLeft: overlap,
                      // CSS vars consumed by the keyframes so each chip animates
                      // to its own final resting angle + arrives from a unique
                      // starting X drift + tumble
                      ['--sl-rot-final']: `${rot}deg`,
                      ['--sl-rot-x-init']: `${rotXInit}deg`,
                      ['--sl-drift-x']: `${drift}px`,
                      ['--sl-delay']: `${delay}s`,
                    }}
                  >
                    <XIcon />
                    {chip}
                  </span>
                );
              })}
            </div>
          </div>

          <p style={{ textAlign: 'center', maxWidth: 780, margin: '44px auto 0', fontSize: 22, fontWeight: 700, lineHeight: 1.5, color: c.text }}>
            There's one reason:{' '}
            <span style={{ color: c.purple }}>your profile isn't written in the words recruiters search.</span>
          </p>

          <div style={{ textAlign: 'center', marginTop: 34 }}>
            <button onClick={goAnalyze} className="sl-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: c.gradient, color: '#fff', padding: '16px 34px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 14px 32px rgba(124,58,237,.32)', fontFamily: 'inherit' }}>
              Get My Free Evaluation <ArrowIcon />
            </button>
            <p style={{ fontSize: 13, color: c.textDim, marginTop: 12 }}>Free. No card. Two minutes.</p>
          </div>
        </div>
      </section>

      {/* ============== SECTION 6a: 4 differentiator cards ============== */}
      <section style={{ padding: '56px 24px', background: c.bgLavender }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 780, margin: '0 auto 52px' }}>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px', marginBottom: 14 }}>
              Optimize your LinkedIn profile for{' '}
              <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>recruiter search.</span>
            </h2>
            <p style={{ fontSize: 18, color: c.textMute, lineHeight: 1.7 }}>The only platform built by recruiters who actually hire.</p>
          </div>

          <div className="sl-feature-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 20, maxWidth: 960, margin: '0 auto' }}>
            {differentiators.map((d) => (
              <div key={d.title} className="sl-feature-card" style={{ background: '#fff', borderRadius: 20, padding: 36, boxShadow: '0 12px 32px rgba(30,15,60,.05)' }}>
                <div className="sl-feature-card-head" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: c.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 8px 18px rgba(124,58,237,.3)' }}>{d.icon}</div>
                  <h3 style={{ fontSize: 19, fontWeight: 800, color: '#000', margin: 0 }}>{d.title}</h3>
                </div>
                <p style={{ fontSize: 14, color: c.textSoft, lineHeight: 1.65 }}>{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SECTION 6b: Sticky-left + scrolling-right 4-step process ============== */}
      <section id="how-it-works" style={{ padding: '56px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div className="sl-process-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,0.85fr) minmax(0,1.15fr)', gap: 56, alignItems: 'start' }}>

            {/* Sticky left */}
            <div className="sl-process-left" style={{ position: 'sticky', top: 96, alignSelf: 'start' }}>
              <h2 style={{ fontSize: 'clamp(30px,3.6vw,46px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px', marginBottom: 16, maxWidth: '100%', overflowWrap: 'break-word' }}>
                Get found and hired.{' '}
                <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Without the guesswork.</span>
              </h2>
              <p style={{ fontSize: 18, color: c.textMute, lineHeight: 1.7, marginBottom: 32 }}>
                Successlane is built by recruiters, not AI. Go from invisible to recruiter calls in 48 hours. No templates. No freelancers. No figuring it out yourself.
              </p>
              <div style={{ background: `radial-gradient(500px 300px at 20% 0%, ${c.navyMid}, ${c.navy} 75%)`, backgroundColor: c.navy, borderRadius: 22, padding: 32, boxShadow: '0 24px 50px rgba(124,58,237,.22)' }}>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 8, maxWidth: '100%', overflowWrap: 'break-word' }}>
                  See where you stand in recruiter search. Fix it in 48 hours.
                </h3>
                <button onClick={goAnalyze} className="sl-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: c.gradient, color: '#fff', padding: '14px 26px', borderRadius: 13, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 12px 28px rgba(124,58,237,.4)', marginTop: 22, fontFamily: 'inherit' }}>
                  Get My Free Evaluation <ArrowIcon />
                </button>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 14 }}>Free. No card. Two minutes.</p>
              </div>
            </div>

            {/* Scrolling right timeline */}
            <div className="sl-timeline-rail" style={{ position: 'relative', paddingLeft: 88 }}>
              <div className="sl-timeline-line" style={{ position: 'absolute', left: 31, top: 26, bottom: 26, width: 2, background: '#e2d7f7' }} />

              {[
                {
                  n: 1,
                  title: 'See where you actually stand.',
                  bullets: [
                    'Your profile scored the way a recruiter reads it',
                    'Missing keywords and weak spots, named specifically',
                  ],
                  visual: (
                    <div style={{ background: 'linear-gradient(160deg,#f5f1fe,#ede7fc)', borderRadius: 16, padding: 22 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: c.textMute }}>Recruiter-view score</span>
                        <span style={{ fontSize: 28, fontWeight: 800, color: c.purple, lineHeight: 1 }}>62<span style={{ fontSize: 14, color: c.textDim, fontWeight: 700 }}>/100</span></span>
                      </div>
                      <div style={{ height: 10, borderRadius: 6, background: '#e2d9f6', overflow: 'hidden', marginBottom: 18 }}>
                        <div style={{ width: '62%', height: '100%', background: 'linear-gradient(90deg,#8b5cf6,#7c3aed)' }} />
                      </div>
                      {[
                        { ok: false, label: 'Headline keywords — missing' },
                        { ok: false, label: 'Search terms — weak' },
                        { ok: true,  label: 'Experience depth — strong' },
                      ].map((row) => (
                        <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: c.textSoft, marginBottom: 9 }}>
                          {row.ok ? <CheckIcon fill={c.green} size={16} /> : <XIcon size={16} />}
                          {row.label}
                        </div>
                      ))}
                    </div>
                  ),
                },
                {
                  n: 2,
                  title: 'Tell us the role you want.',
                  bullets: [
                    'Share your LinkedIn, resume, and target role',
                    'We handle everything from here',
                  ],
                  visual: (
                    <div style={{ background: 'linear-gradient(160deg,#f5f1fe,#ede7fc)', borderRadius: 16, padding: 22 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: c.purpleSoft, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>Target role</div>
                      <div style={{ background: '#fff', border: '1px solid #e2d9f6', borderRadius: 11, padding: '13px 16px', fontSize: 15, fontWeight: 600, color: '#2b2440', marginBottom: 14 }}>Senior Product Manager, Consumer Internet</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {['resume.pdf', 'linkedin.com/in/you'].map((f) => (
                          <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2d9f6', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 600, color: c.textSoft }}>
                            <CheckIcon fill={c.green} size={16} /> {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  ),
                },
                {
                  n: 3,
                  title: 'A recruiter certifies it.',
                  bullets: [
                    'A working recruiter from your industry reviews every section',
                    'They sign off before you see it',
                  ],
                  visual: (
                    <div style={{ background: 'linear-gradient(160deg,#f5f1fe,#ede7fc)', borderRadius: 16, padding: 24, display: 'flex', alignItems: 'center', gap: 18 }}>
                      <div style={{ width: 56, height: 56, borderRadius: 14, background: c.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 10px 22px rgba(124,58,237,.3)' }}>
                        <ShieldIcon size={30} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: c.greenLite, color: c.greenDeep, fontSize: 12, fontWeight: 700, padding: '4px 11px', borderRadius: 100, marginBottom: 8 }}>
                          <CheckIcon fill={c.greenDeep} size={14} /> Certified
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#2b2440' }}>Reviewed by a Senior Recruiter</div>
                        <div style={{ fontSize: 13, color: c.purpleSoft }}>Consumer Tech · 400+ placements</div>
                      </div>
                    </div>
                  ),
                },
                {
                  n: 4,
                  accent: true,
                  title: 'Recruiters find you.',
                  bullets: [
                    "InMails, calls, conversations you didn't apply for",
                    'A profile that keeps working long after this search is over',
                  ],
                  visual: (
                    <div style={{ background: 'linear-gradient(160deg,#f5f1fe,#ede7fc)', borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {[
                        { init: 'TA', bg: '#ede9fe', color: '#6d28d9', title: 'Talent Partner · Consumer Tech', msg: 'Are you open to a Senior PM role?' },
                        { init: 'HR', bg: '#e0e7ff', color: '#4338ca', title: 'Head of TA · Fintech', msg: 'Your profile came up in our search — free to talk?' },
                      ].map((m) => (
                        <div key={m.init} style={{ display: 'flex', alignItems: 'center', gap: 13, background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 6px 16px rgba(30,15,60,.06)' }}>
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: m.bg, color: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{m.init}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#2b2440' }}>{m.title}</div>
                            <div style={{ fontSize: 13, color: c.purpleSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.msg}</div>
                          </div>
                          <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.green, flexShrink: 0 }} />
                        </div>
                      ))}
                    </div>
                  ),
                },
              ].map((step) => (
                <div key={step.n} style={{ position: 'relative', marginBottom: step.n === 4 ? 0 : 36 }}>
                  <div className="sl-timeline-dot" style={{ position: 'absolute', left: -72, top: 6, width: 48, height: 48, borderRadius: '50%', background: step.accent ? c.gradient : c.navy, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, boxShadow: `0 0 0 5px #f7f3fe, ${step.accent ? '0 10px 22px rgba(124,58,237,.4)' : '0 8px 18px rgba(26,12,56,.3)'}` }}>{step.n}</div>
                  <div className="sl-timeline-card" style={{ background: '#fff', border: '1px solid #efe9fb', borderRadius: 22, padding: 34, boxShadow: '0 18px 44px rgba(30,15,60,.07)' }}>
                    <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.4px', marginBottom: 10 }}>{step.title}</h3>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 22px' }}>
                      {step.bullets.map((b) => (
                        <li key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 15, color: c.textSoft, lineHeight: 1.5, marginBottom: 8 }}>
                          <CheckIcon fill={c.green} size={16} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    {step.visual}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== SECTION 7: Recruiter panel (draggable rail, redesign) ============== */}
      <RecruiterPanel goAnalyze={goAnalyze} />

      {/* ============== SECTION 8: Before / After — LinkedIn profile mockups (side-by-side, not tabs) ============== */}
      <section style={{ padding: '56px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 44px' }}>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px', marginBottom: 16 }}>
              <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>This is how we transform</span>
            </h2>
            <p style={{ fontSize: 15, color: c.textDim, lineHeight: 1.7 }}>Before and After</p>
          </div>

          {/* Single before/after graphic (designed asset) replacing the
              previous coded two-panel mockup — the image already carries
              its own "BEFORE"/"AFTER" labels and stat callouts. Capped at
              880px on desktop (rather than stretching to the 1180px section
              width) since the source is a dense 4096x2244 composite that
              reads as oversized/blurry past that width; scales fluidly down
              to mobile via width:100%. */}
          <div style={{ maxWidth: 880, margin: '0 auto' }}>
            <img
              src="/images/landing/before-after-mockup-v2.png"
              alt="Before: a bare LinkedIn profile for Sarah Johnson with 43 profile views and 12 search appearances. After: a Successlane-optimized profile, recruiter-certified, with a custom banner, complete About and Top Skills sections, 2,847 profile views and 1,126 search appearances."
              loading="lazy"
              style={{ width: '100%', maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 20, boxShadow: '0 20px 50px rgba(30,15,60,.12)' }}
            />
          </div>

          <p style={{ textAlign: 'center', maxWidth: 620, margin: '32px auto 0', fontSize: 15, color: c.textDim }}>
            Nothing was invented. The work was always there. It just wasn't written where recruiters look.
          </p>
        </div>
      </section>

      {/* ============== SECTION 9: Testimonials (arc carousel, redesign) ============== */}
      <TestimonialsArc />

      {/* ============== SECTION 10: Company logos marquee — "our users have landed at top companies" ============== */}
      <section style={{ padding: '44px 0', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto 40px', padding: '0 24px', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(26px,3.8vw,42px)', fontWeight: 800, letterSpacing: '-1px', color: c.text, lineHeight: 1.15, marginBottom: 12 }}>
            Trusted by job seekers who've landed at top companies
          </h2>
          <p style={{ fontSize: 15, color: c.textMute, fontStyle: 'italic' }}>
            Our users have secured positions at industry-leading companies such as
          </p>
        </div>
        <div className="sl-marquee-wrap" style={{ overflow: 'hidden' }}>
          <div className="sl-marquee-track" style={{ display: 'flex', alignItems: 'center', gap: 72, width: 'max-content', ['--sl-dur']: '38s' }}>
            {/* Duplicated once for seamless loop */}
            {[...Array(2)].flatMap((_, dupIdx) => [
              // Real brand marks — Boeing/Amex/Zomato sourced from the
              // simple-icons brand-icon set (official hex per their metadata),
              // EY sourced from Wikimedia Commons "EY logo 2019.svg" (the
              // current official mark). Amazon keeps the existing hand-built
              // wordmark+arrow, which was already accurate.
              <svg key={`bo-${dupIdx}`} viewBox="0 0 24 24" style={{ width: 42, height: 42, flexShrink: 0 }} fill="#1D439C">
                <title>Boeing</title>
                <path d="M6.9044 17.2866a6.0512 6.0512 0 01-3.7595-1.3008c1.2048-2.7146 3.6545-6.3581 6.998-9.9166a6.0702 6.0702 0 012.2617 7.729c-1.0599-.49-2.0497-1.106-2.8876-1.8798l1.8307 3.4375a6.0582 6.0582 0 01-4.4433 1.9307M.8292 11.2115a6.0752 6.0752 0 016.0762-6.0772c.8998 0 1.7527.196 2.5226.546-3.2935 2.9095-5.8432 6.293-7.353 9.2177a6.0512 6.0512 0 01-1.2458-3.6875m12.3403 2.9126a6.862 6.862 0 00.6419-2.9126c0-2.3997-1.2248-4.5144-3.0846-5.7532a49.6072 49.6072 0 013.5825-3.3416A31.1727 31.1727 0 0010.11 5.0903a6.907 6.907 0 00-8.4368 10.6265C.3493 18.5795.1193 20.8781 1.285 21.654c1.2489.832 3.9625-.6769 5.5903-3.1345 0 0-2.5177 2.2736-3.9015 1.7517-.8519-.322-.8549-1.6248-.152-3.4925a6.871 6.871 0 004.0835 1.3378c1.8937 0 3.6065-.7599 4.8533-1.9917l.245.462c3.0095-.245 11.9963-.483 11.9963-.483 0-.431-5.9502-.04-10.8325-1.9797" />
              </svg>,
              <span key={`am-${dupIdx}`} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 40, fontWeight: 700, color: '#111', letterSpacing: '-2px', lineHeight: 1 }}>amazon</span>
                <svg viewBox="0 0 90 12" style={{ width: 90, height: 10, marginTop: 2 }}>
                  <path d="M2 4 Q45 14 88 4" stroke="#FF9900" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                  <path d="M84 4 l4 0 l-2 3 z" fill="#FF9900" />
                </svg>
              </span>,
              <svg key={`ax-${dupIdx}`} viewBox="0 0 24 24" style={{ width: 44, height: 44, flexShrink: 0 }} fill="#2E77BC">
                <title>American Express</title>
                <path d="M16.015 14.378c0-.32-.135-.496-.344-.622-.21-.12-.464-.135-.81-.135h-1.543v2.82h.675v-1.027h.72c.24 0 .39.024.478.125.12.13.104.38.104.55v.35h.66v-.555c-.002-.25-.017-.376-.108-.516-.06-.08-.18-.18-.33-.234l.02-.008c.18-.072.48-.297.48-.747zm-.87.407l-.028-.002c-.09.053-.195.058-.33.058h-.81v-.63h.824c.12 0 .24 0 .33.05.098.048.156.147.15.255 0 .12-.045.215-.134.27zM20.297 15.837H19v.6h1.304c.676 0 1.05-.278 1.05-.884 0-.28-.066-.448-.187-.582-.153-.133-.392-.193-.73-.207l-.376-.015c-.104 0-.18 0-.255-.03-.09-.03-.15-.105-.15-.21 0-.09.017-.166.09-.21.083-.046.177-.066.272-.06h1.23v-.602h-1.35c-.704 0-.958.437-.958.84 0 .9.776.855 1.407.87.104 0 .18.015.225.06.046.03.082.106.082.18 0 .077-.035.15-.08.18-.06.053-.15.07-.277.07zM0 0v10.096L.81 8.22h1.75l.225.464V8.22h2.043l.45 1.02.437-1.013h6.502c.295 0 .56.057.756.236v-.23h1.787v.23c.307-.17.686-.23 1.12-.23h2.606l.24.466v-.466h1.918l.254.465v-.466h1.858v3.948H20.87l-.36-.6v.585h-2.353l-.256-.63h-.583l-.27.614h-1.213c-.48 0-.84-.104-1.08-.24v.24h-2.89v-.884c0-.12-.03-.12-.105-.135h-.105v1.036H6.067v-.48l-.21.48H4.69l-.202-.48v.465H2.235l-.256-.624H1.4l-.256.624H0V24h23.786v-7.108c-.27.135-.613.18-.973.18H21.09v-.255c-.21.165-.57.255-.914.255H14.71v-.9c0-.12-.018-.12-.12-.12h-.075v1.022h-1.8v-1.066c-.298.136-.643.15-.928.136h-.214v.915h-2.18l-.54-.617-.57.6H4.742v-3.93h3.61l.518.602.554-.6h2.412c.28 0 .74.03.942.225v-.24h2.177c.202 0 .644.045.903.225v-.24h3.265v.24c.163-.164.508-.24.803-.24h1.89v.24c.194-.15.464-.24.84-.24h1.176V0H0zM21.156 14.955c.004.005.006.012.01.016.01.01.024.01.032.02l-.042-.035zM23.828 13.082h.065v.555h-.065zM23.865 15.03v-.005c-.03-.025-.046-.048-.075-.07-.15-.153-.39-.215-.764-.225l-.36-.012c-.12 0-.194-.007-.27-.03-.09-.03-.15-.105-.15-.21 0-.09.03-.16.09-.204.076-.045.15-.05.27-.05h1.223v-.588h-1.283c-.69 0-.96.437-.96.84 0 .9.78.855 1.41.87.104 0 .18.015.224.06.046.03.076.106.076.18 0 .07-.034.138-.09.18-.045.056-.136.07-.27.07h-1.288v.605h1.287c.42 0 .734-.118.9-.36h.03c.09-.134.135-.3.135-.523 0-.24-.045-.39-.135-.526zM18.597 14.208v-.583h-2.235V16.458h2.235v-.585h-1.57v-.57h1.533v-.584h-1.532v-.51M13.51 8.787h.685V11.6h-.684zM13.126 9.543l-.007.006c0-.314-.13-.5-.34-.624-.217-.125-.47-.135-.81-.135H10.43v2.82h.674v-1.034h.72c.24 0 .39.03.487.12.122.136.107.378.107.548v.354h.677v-.553c0-.25-.016-.375-.11-.516-.09-.107-.202-.19-.33-.237.172-.07.472-.3.472-.75zm-.855.396h-.015c-.09.054-.195.056-.33.056H11.1v-.623h.825c.12 0 .24.004.33.05.09.04.15.128.15.25s-.047.22-.134.266zM15.92 9.373h.632v-.6h-.644c-.464 0-.804.105-1.02.33-.286.3-.362.69-.362 1.11 0 .512.123.833.36 1.074.232.238.645.31.97.31h.78l.255-.627h1.39l.262.627h1.36v-2.11l1.272 2.11h.95l.002.002V8.786h-.684v1.963l-1.18-1.96h-1.02V11.4L18.11 8.744h-1.004l-.943 2.22h-.3c-.177 0-.362-.03-.468-.134-.125-.15-.186-.36-.186-.662 0-.285.08-.51.194-.63.133-.135.272-.165.516-.165zm1.668-.108l.464 1.118v.002h-.93l.466-1.12zM2.38 10.97l.254.628H4V9.393l.972 2.205h.584l.973-2.202.015 2.202h.69v-2.81H6.118l-.807 1.904-.876-1.905H3.343v2.663L2.205 8.787h-.997L.01 11.597h.72l.26-.626h1.39zm-.688-1.705l.46 1.118-.003.002h-.915l.457-1.12zM11.856 13.62H9.714l-.85.923-.825-.922H5.346v2.82H8l.855-.932.824.93h1.302v-.94h.838c.6 0 1.17-.164 1.17-.945l-.006-.003c0-.78-.598-.93-1.128-.93zM7.67 15.853l-.014-.002H6.02v-.557h1.47v-.574H6.02v-.51H7.7l.733.82-.764.824zm2.642.33l-1.03-1.147 1.03-1.108v2.253zm1.553-1.258h-.885v-.717h.885c.24 0 .42.098.42.344 0 .243-.15.372-.42.372zM9.967 9.373v-.586H7.73V11.6h2.237v-.58H8.4v-.564h1.527V9.88H8.4v-.507" />
              </svg>,
              <svg key={`ey-${dupIdx}`} viewBox="0 0 68.67 69.32" style={{ width: 42, height: 42, flexShrink: 0 }}>
                <title>EY</title>
                <path d="M11.09 61.4h17.37v7.92H.67V34.9h19.7l4.61 7.92H11.1v5.68h12.56v7.22H11.1zm35.86-26.5l-5.9 11.23-5.88-11.23H23.65l12.13 20.82v13.6h10.4v-13.6L58.31 34.9z" fill="#161d23" fillRule="evenodd" />
                <path fill="#ffe600" fillRule="evenodd" d="M68.67 12.81V0L0 24.83z" />
              </svg>,
              <svg key={`zm-${dupIdx}`} viewBox="0 0 24 24" style={{ width: 42, height: 42, flexShrink: 0 }} fill="#E23744">
                <title>Zomato</title>
                <path d="M19.615 9.45l-1.258.473-.167.71-.446.021-.115.978h.408l-.211 1.51c-.131.939.036 1.381.865 1.381.488 0 .91-.175 1.135-.297l.145-.9c-.167.083-.436.19-.618.19-.247 0-.276-.13-.225-.488l.189-1.396h.843c.03-.206.131-.877.16-1h-.865zm-3.779 1.002c-.115.002-.236.01-.361.026a3.592 3.592 0 0 0-1.347.432l.26.789c.269-.15.615-.28.978-.326.538-.066.757.1.79.375.014.109.004.199-.005.289l-.014.056a3.46 3.46 0 0 0-1.097-.036c-.518.063-.943.273-1.204.6a1.324 1.324 0 0 0-.225 1.034c.127.583.553.84 1.199.76.45-.055.812-.27 1.076-.63a2.665 2.665 0 0 1-.03.304 1.74 1.74 0 0 1-.072.29l1.244.001a3.657 3.657 0 0 1-.001-.365c.036-.459.118-1.143.247-2.051a2.397 2.397 0 0 0-.002-.59c-.08-.644-.628-.969-1.436-.958zm6.536.063c-1.194 0-2.107 1.067-2.107 2.342 0 .959.552 1.693 1.628 1.693 1.2 0 2.107-1.067 2.107-2.35 0-.95-.538-1.685-1.628-1.685zm-11.777.041c-.538 0-1.12.465-1.52 1.236.102-.504.08-1.076.051-1.198a8.964 8.964 0 0 1-1.287.122 6.9 6.9 0 0 1-.073 1.243l-.167 1.145c-.066.45-.138.969-.211 1.297h1.353c.007-.199.058-.511.094-.786l.116-.786c.095-.511.502-1.114.815-1.114.182 0 .175.176.124.504l-.131.885c-.066.45-.138.969-.211 1.297h1.367c.008-.199.051-.512.088-.786l.116-.786c.094-.512.502-1.114.814-1.114.182 0 .175.168.146.396l-.327 2.29H13l.438-2.609c.095-.649.044-1.236-.676-1.236-.523 0-1.09.443-1.49 1.182.087-.61.036-1.182-.677-1.182zm-4.88.008c-1.177 0-2.08 1.053-2.08 2.312 0 .946.546 1.67 1.608 1.67 1.185 0 2.08-1.052 2.08-2.319 0-.938-.531-1.663-1.607-1.663zm-5.126.091c-.05.39-.102.778-.175 1.13.328-.008.619-.016 1.411-.016l-1.81 1.96-.015.703c.444-.03.997-.039 1.63-.039.566 0 1.134.008 1.497.039.065-.458.13-.763.21-1.137-.275.015-.755.023-1.512.023l1.81-1.969.023-.694c-.437.023-.83.03-1.52.03-.749 0-.975-.007-1.549-.03zm4.988.927c.255 0 .408.228.408.701 0 .687-.276 1.251-.626 1.251-.261 0-.414-.236-.414-.702 0-.694.283-1.25.632-1.25zm16.629 0c.254 0 .407.228.407.701 0 .687-.276 1.251-.625 1.251-.262 0-.415-.236-.415-.702 0-.694.284-1.25.633-1.25zM15.51 12.64c.206-.003.403.024.55.058l-.013.118c-.075.44-.39.881-.848.938-.31.037-.578-.148-.608-.39a.538.538 0 0 1 .114-.41c.117-.159.336-.268.599-.3.069-.009.138-.013.206-.014Z" />
              </svg>,
            ])}
          </div>
        </div>
      </section>

      {/* ============== SECTION 11: Combined Promise + Pricing ============== */}
      <section id="pricing" style={{ padding: '56px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 780, margin: '0 auto 40px' }}>
            <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 2, color: c.purple, fontWeight: 700, marginBottom: 14 }}>OUR PROMISE. OUR PRICE.</div>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px', marginBottom: 16 }}>
              No other service does this at this price and quality.{' '}
              <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>If you find one, we'll do yours free.</span>
            </h2>
          </div>

          <div className="sl-pricing-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 24, maxWidth: 820, margin: '0 auto' }}>
            {/* Free */}
            <div style={{ background: '#fff', borderRadius: 24, padding: 40, boxShadow: '0 12px 32px rgba(30,15,60,.05)' }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Free Evaluation</h3>
              <p style={{ fontSize: 14, color: c.textSoft, lineHeight: 1.6, marginBottom: 20 }}>See where you actually stand in recruiter search.</p>
              <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 4 }}>₹0</div>
              <p style={{ fontSize: 13, color: c.textDim, marginBottom: 24 }}>Two minutes. No card. No signup.</p>
              <button onClick={goAnalyze} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f2f1f8', color: c.text, padding: '15px 24px', borderRadius: 14, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Get My Free Evaluation</button>
              <div style={{ marginTop: 24, paddingTop: 24, borderTop: `1px solid ${c.bgFaint}` }}>
                {['Recruiter level analysis', 'Missing keywords', 'Section wise evaluation', 'Weak spots'].map((f) => (
                  <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: c.textSoft, marginBottom: 11 }}>
                    <CheckIcon fill={c.green} size={16} /> {f}
                  </div>
                ))}
              </div>
            </div>

            {/* Full Rewrite + 100-day plan */}
            <div style={{ background: '#fff', border: `2px solid ${c.purple}`, borderRadius: 24, padding: 40, position: 'relative', boxShadow: '0 24px 50px rgba(124,58,237,.18)' }}>
              <span style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: c.gradient, color: '#fff', fontSize: 12, fontWeight: 700, padding: '6px 18px', borderRadius: 100, whiteSpace: 'nowrap', boxShadow: '0 8px 18px rgba(124,58,237,.35)' }}>Most Popular</span>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Full Rewrite + 100-Day Plan</h3>
              <p style={{ fontSize: 14, color: c.textSoft, lineHeight: 1.6, marginBottom: 20 }}>
                A recruiter rewrites your LinkedIn, certifies it, and backs you for 100 days until recruiters call.
              </p>
              {/* MRP-style strikethrough — appears whenever the backend says
                  there's a comparison price (pricing.compareAt), regardless
                  of whether admin is running an internal discount off the
                  standard price or comparing against the market rate — both
                  modes render identically here since the backend already
                  picked the right number. Falls back to a plain price the
                  rest of the time. */}
              {pricing.compareAt ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-1.5px' }}>₹{effectivePrice.toLocaleString('en-IN')}</span>
                  <span style={{ fontSize: 20, fontWeight: 700, color: c.textDim, textDecoration: 'line-through' }}>₹{pricing.compareAt.toLocaleString('en-IN')}</span>
                  {discountPct > 0 && (
                    <span style={{ background: c.greenLite, color: c.greenDeep, fontSize: 12, fontWeight: 800, padding: '4px 10px', borderRadius: 100, whiteSpace: 'nowrap' }}>
                      {discountPct}% OFF
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 4 }}>₹{effectivePrice.toLocaleString('en-IN')}</div>
              )}
              <p style={{ fontSize: 13, color: c.textDim, marginBottom: 24 }}>Delivered in 48 hours</p>
              <button onClick={goCheckout} className="sl-cta-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: c.gradient, color: '#fff', padding: '15px 24px', borderRadius: 14, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 12px 26px rgba(124,58,237,.35)', fontFamily: 'inherit' }}>
                Get My Certified Rewrite <ArrowIcon />
              </button>
              <div style={{ marginTop: 24, paddingTop: 24, borderTop: `1px solid ${c.bgFaint}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.textDim, marginBottom: 11 }}>Everything in Free, plus:</div>
                {[
                  'Complete LinkedIn rewrite',
                  'Recruiter-certified',
                  '100-day action plan',
                  'Copy-paste ready',
                  '7 days free revision',
                ].map((f, i) => (
                  <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: c.textSoft, marginBottom: 11 }}>
                    {i < 2 ? <GoldCheckIcon size={18} /> : <CheckIcon fill={c.green} size={16} />} {f}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p style={{ textAlign: 'center', maxWidth: 720, margin: '32px auto 0', fontSize: 14, color: c.textMute, fontWeight: 600 }}>
            Join <strong style={{ color: c.text }}>1,928</strong> professionals who stopped applying and started getting found.
          </p>
        </div>
      </section>

      {/* ============== SECTION 12: Dark CTA band (before FAQ) ============== */}
      <section style={{ padding: '16px 18px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', background: `radial-gradient(800px 400px at 50% 0%, ${c.navyMid} 0%, ${c.navy} 75%)`, backgroundColor: c.navy, borderRadius: 28, color: '#fff', textAlign: 'center', padding: '56px 24px' }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,48px)', fontWeight: 800, letterSpacing: '-1.4px', marginBottom: 16, lineHeight: 1.15 }}>
              Successlane is built by recruiters. <span style={{ color: 'rgba(255,255,255,.6)' }}>Not Generic AI. Not freelancers.</span>
            </h2>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,.75)', marginBottom: 32, lineHeight: 1.7 }}>
              With Successlane, you go from invisible to recruiter calls in 48 hours. A recruiter from your industry rewrites your profile, certifies every word, and backs you with a 100-day plan. This is not a template. This is not a chatbot. This is what actually works.
            </p>
            <button onClick={goAnalyze} className="sl-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: c.gradient, color: '#fff', padding: '17px 40px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 14px 34px rgba(124,58,237,.5)', fontFamily: 'inherit' }}>
              Get My Free Evaluation <ArrowIcon size={18} />
            </button>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.45)', marginTop: 14 }}>
              No credit card. No signup. Just a real recruiter-voice read of your profile. Free. Always.
            </p>
          </div>
        </div>
      </section>

      {/* ============== SECTION 13: Two-path closing CTA (moved before FAQ) ============== */}
      <section style={{ padding: '56px 24px', background: c.bgLavender }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 48px' }}>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, letterSpacing: '-1.4px', lineHeight: 1.1 }}>
              You have <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>two options.</span>
            </h2>
          </div>

          <div className="sl-closing-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 24, maxWidth: 940, margin: '0 auto', alignItems: 'stretch' }}>
            {/* Option A — muted */}
            <div style={{ background: '#f2f1f8', border: '1px solid #e2dfeb', borderRadius: 22, padding: 40, color: c.textMute }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, color: c.textDim, fontWeight: 700, marginBottom: 14 }}>Option A</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 18, color: c.textMid, lineHeight: 1.3 }}>Keep applying the way you have been.</h3>
              <p style={{ fontSize: 15, color: c.textDim, lineHeight: 1.7, marginBottom: 8 }}>Send 200 more applications. Wait for luck.</p>
              <p style={{ fontSize: 15, color: c.textDim, lineHeight: 1.7 }}>Hope someone opens your profile.</p>
            </div>

            {/* Option B — accent/dominant */}
            <div style={{ background: `radial-gradient(800px 400px at 50% 0%, ${c.navyMid} 0%, ${c.navy} 75%)`, backgroundColor: c.navy, borderRadius: 22, padding: 40, color: '#fff', boxShadow: '0 24px 60px rgba(26,12,56,.4)' }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, color: '#c4b5fd', fontWeight: 700, marginBottom: 14 }}>Option B</div>
              <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 18, lineHeight: 1.3 }}>Give us 48 hours.</h3>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,.8)', lineHeight: 1.7, marginBottom: 8 }}>We rewrite your profile. A recruiter certifies it.</p>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,.8)', lineHeight: 1.7, marginBottom: 28 }}>You get 4X visibility and offers start coming to you.</p>
              <button onClick={goAnalyze} className="sl-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: c.gradient, color: '#fff', padding: '16px 32px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 14px 34px rgba(124,58,237,.5)', fontFamily: 'inherit' }}>
                Get My Free Evaluation <ArrowIcon size={18} />
              </button>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 14 }}>Free. No card. Two minutes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== SECTION 14: FAQ (moved to final section) ============== */}
      <section id="faq" style={{ padding: '56px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 44px' }}>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px' }}>
              A few things people ask{' '}
              <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>before they start.</span>
            </h2>
          </div>
          <div style={{ maxWidth: 740, margin: '0 auto' }}>
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} style={{ background: c.bgLavender, borderRadius: 16, marginBottom: 12, padding: '0 24px' }}>
                  <button
                    className="sl-faq-btn"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '22px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 700, color: c.text, textAlign: 'left', fontFamily: 'inherit', gap: 16 }}
                    aria-expanded={open}
                  >
                    <span style={{ minWidth: 0, flex: 1 }}>{f.q}</span>
                    <span style={{ fontSize: 26, fontWeight: 400, color: c.purple, lineHeight: 1, flexShrink: 0, transition: 'transform .2s', display: 'inline-block', transform: `rotate(${open ? 45 : 0}deg)` }}>+</span>
                  </button>
                  {open && <p style={{ paddingBottom: 24, fontSize: 15, color: c.textSoft, lineHeight: 1.7 }}>{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== FOOTER (existing content, restyled) ============== */}
      <footer style={{ padding: '40px 24px 56px', background: c.bgWhite, borderTop: `1px solid ${c.bgFaint}` }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <Logo height={20} />
            <span style={{ fontSize: 13, color: c.textDim }}>© {new Date().getFullYear()} Successlane. All rights reserved.</span>
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <a href="/about"        style={{ fontSize: 13, color: c.textDim, textDecoration: 'none' }}>About</a>
            <a href="/privacy"      style={{ fontSize: 13, color: c.textDim, textDecoration: 'none' }}>Privacy</a>
            <a href="/terms"        style={{ fontSize: 13, color: c.textDim, textDecoration: 'none' }}>Terms</a>
            <a href="/refund"       style={{ fontSize: 13, color: c.textDim, textDecoration: 'none' }}>Refund</a>
            <a href="/cancellation" style={{ fontSize: 13, color: c.textDim, textDecoration: 'none' }}>Cancellation</a>
            <a href="/shipping"     style={{ fontSize: 13, color: c.textDim, textDecoration: 'none' }}>Shipping</a>
            <a href="mailto:contact@successlane.io" style={{ fontSize: 13, color: c.textDim, textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLanding;
